import { memo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";
import { Button } from "app/components/ui/Button/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconHandLuggageAvailable from "app/pages/.shared/IconHandLuggageAvailable";
import IconHandLuggageUnavailable from "app/pages/.shared/IconHandLuggageUnavailable";
import { getFlightNegotiatedLabelKey } from "app/utils/smartDPUtils";
import "./FlightBaggageBlock.scss";
import isEmpty from "lodash/isEmpty";
import {
	BaggageLFilledIcon,
	BaggageLDisabledFilledIcon,
	BaggageMFilledIcon,
	BaggageMDisabledFilledIcon,
} from "app/icons/travel";
import AirplaneFilledIcon from "app/icons/travel/AirplaneFilledIcon";
import { ICON_SIZES } from "app/constants";

const FlightBaggageBlock = ({ flight = {}, onViewDetail, className, ...props }) => {
	const handleViewDetail = useCallback(() => {
		if (onViewDetail) {
			onViewDetail();
		}
	}, [onViewDetail]);

	const handleButtonClick = useCallback(
		e => {
			e.stopPropagation(); // Empêcher la propagation pour éviter le double click
			handleViewDetail();
		},
		[handleViewDetail]
	);

	const blockClasses = classNames("flight-baggage-block", className);

	// Utiliser directement la logique de getFlightNegotiatedLabelKey
	const labelKey = getFlightNegotiatedLabelKey(flight);

	// Extraire les données pour les valeurs du message
	const checkedBaggage = flight?.flightOptions?.flightBagageOption;
	const handBaggage = flight?.flightOptions?.flightBagageOption?.handBaggage;
	const cabinBaggage = flight?.flightOptions?.flightBagageOption?.cabinBaggage;

	const isBaggageFree = flight?.flightOptions?.flightBagageOptions?.[0]?.pricePerBooking === 0;

	return (
		<div className={blockClasses} data-testid="flight-baggage-block" {...props}>
			<div
				className="flight-baggage-block__content"
				onClick={handleViewDetail}
				role={onViewDetail ? "button" : ""}
				tabIndex={onViewDetail ? 0 : -1}
				data-testid="flight-baggage-content"
				onKeyDown={
					onViewDetail
						? e => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleViewDetail();
								}
							}
						: undefined
				}
			>
				<div className="flight-baggage-block__icon">
					<AirplaneFilledIcon size={ICON_SIZES.XS} />
				</div>

				<div className="flight-baggage-block__text">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="flight-baggage-block__label"
					>
						<FormattedMessage id={labelKey} />
						{onViewDetail && (
							<Button
								variant="borderless"
								size="md"
								onClick={handleButtonClick}
								data-testid="view-flight-detail"
								className="flight-baggage-block__detail-link"
							>
								<FormattedMessage id="sdp.listing.product.view.flight.button" />
							</Button>
						)}
					</Typography>
				</div>

				<div className="flight-baggage-block__baggage-icons" data-testid="baggage-icons">
					{/* do not display hand baggage icon if we do not have any data on it */}
					{!isEmpty(handBaggage) && (
						<div
							className={classNames("flight-baggage-block__icon", {
								"flight-baggage-block__icon--unavailable": !handBaggage?.included,
							})}
							data-testid="hand-baggage-icon"
						>
							{handBaggage?.included ? (
								<IconHandLuggageAvailable size={ICON_SIZES.XS} />
							) : (
								<IconHandLuggageUnavailable size={ICON_SIZES.XS} />
							)}
						</div>
					)}

					<div
						className={classNames("flight-baggage-block__icon", {
							"flight-baggage-block__icon--unavailable": !cabinBaggage?.included,
						})}
						data-testid="cabin-baggage-icon"
					>
						{cabinBaggage?.included ? (
							<BaggageMFilledIcon size={ICON_SIZES.XS} />
						) : (
							<BaggageMDisabledFilledIcon size={ICON_SIZES.XS} />
						)}
					</div>

					<div
						className={classNames("flight-baggage-block__icon", {
							"flight-baggage-block__icon--unavailable":
								!checkedBaggage?.included && !isBaggageFree,
							"flight-baggage-block__icon--free": isBaggageFree,
						})}
						data-testid="checkin-baggage-icon"
					>
						{checkedBaggage?.included || isBaggageFree ? (
							<BaggageLFilledIcon size={ICON_SIZES.XS} />
						) : (
							<BaggageLDisabledFilledIcon size={ICON_SIZES.XS} />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

FlightBaggageBlock.propTypes = {
	flight: PropTypes.object,
	onViewDetail: PropTypes.func,
	className: PropTypes.string,
};

export default memo(FlightBaggageBlock);
